import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';

interface Ayar {
    id: number;
    aktif: boolean;
    firmaID: number;
    username: string | null;
    password: string | null;
    brandCode: string | null;
}

const IysAktarimAyarlari: React.FC = () => {
    const [ayar, setAyar] = useState<Ayar>({
        id: 0,
        aktif: false,
        firmaID: 1,
        username: '',
        password: '',
        brandCode: '',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [aktariliyor, setAktariliyor] = useState(false);
    const [bekleyenSayi, setBekleyenSayi] = useState<number | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [aktarimSonuc, setAktarimSonuc] = useState<{
        message: string;
        uyari: boolean;
        sonuc?: {
            toplamBekleyen: number;
            basarili: number;
            hatali: number;
            aktarimPasif: boolean;
            yapılandırmaEksik: boolean;
            hataDetaylari: string[];
        };
    } | null>(null);

    useEffect(() => {
        fetchAyarlar();
        fetchBekleyenSayi();
    }, []);

    const fetchAyarlar = async () => {
        try {
            const res = await axios.get('/api/IysAktarimAyarlari');
            if (res.data) setAyar(res.data);
        } catch {
            toast.error('Ayarlar yüklenemedi.');
        } finally {
            setLoading(false);
        }
    };

    const fetchBekleyenSayi = async () => {
        try {
            const res = await axios.get('/api/IysAktarimAyarlari/bekleyen-sayi');
            setBekleyenSayi(res.data.sayi);
        } catch {}
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.post('/api/IysAktarimAyarlari', {
                aktif: ayar.aktif,
                firmaID: ayar.firmaID,
                username: ayar.username || null,
                password: ayar.password || null,
                brandCode: ayar.brandCode || null,
            });
            toast.success('Ayarlar kaydedildi.');
        } catch {
            toast.error('Kaydetme hatası.');
        } finally {
            setSaving(false);
        }
    };

    const handleAktarSimdi = async () => {
        setAktariliyor(true);
        setAktarimSonuc(null);
        try {
            const res = await axios.post('/api/IysAktarimAyarlari/aktar-simdi');
            setAktarimSonuc(res.data);
            fetchBekleyenSayi();
        } catch {
            toast.error('Aktarım sırasında hata oluştu.');
        } finally {
            setAktariliyor(false);
        }
    };

    if (loading) {
        return <div className="text-center p-4"><i className="fas fa-spinner fa-spin me-2" />Yükleniyor...</div>;
    }

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h5 className="fw-bold mb-0" style={{ color: '#1e293b' }}>
                        <i className="fas fa-share-alt me-2" style={{ color: '#0ea5e9' }} />
                        İYS Aktarım Ayarları
                    </h5>
                    <small className="text-muted">NetGSM üzerinden İYS izinlerini otomatik aktarın</small>
                </div>
                {bekleyenSayi !== null && (
                    <span className={`badge ${bekleyenSayi > 0 ? 'bg-warning text-dark' : 'bg-success'} fs-6`}>
                        {bekleyenSayi} bekleyen kayıt
                    </span>
                )}
            </div>

            {/* Aktif/Pasif */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body d-flex align-items-center justify-content-between" style={{ padding: '14px 20px' }}>
                    <div>
                        <div className="fw-semibold" style={{ color: '#1e293b' }}>IYS Aktarımı</div>
                        <small className="text-muted">Aktif olduğunda IYS izinleri NetGSM'e otomatik aktarılır</small>
                    </div>
                    <div className="form-check form-switch mb-0">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="aktifSwitch"
                            checked={ayar.aktif}
                            onChange={e => setAyar(p => ({ ...p, aktif: e.target.checked }))}
                            style={{ width: 44, height: 22, cursor: 'pointer' }}
                        />
                    </div>
                </div>
            </div>

            {/* Firma seçimi */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body" style={{ padding: '14px 20px' }}>
                    <label className="form-label fw-semibold" style={{ color: '#1e293b', fontSize: 13 }}>
                        Aktarım Firması
                    </label>
                    <select
                        className="form-select form-select-sm"
                        value={ayar.firmaID}
                        onChange={e => setAyar(p => ({ ...p, firmaID: Number(e.target.value) }))}
                        style={{ maxWidth: 280 }}
                    >
                        <option value={1}>NET GSM</option>
                    </select>
                </div>
            </div>

            {/* Kimlik bilgileri */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body" style={{ padding: '14px 20px' }}>
                    <h6 className="fw-semibold mb-3" style={{ color: '#475569', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                        NetGSM Kimlik Bilgileri
                    </h6>
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label" style={{ fontSize: 12, color: '#64748b' }}>
                                Kullanıcı Adı (username)
                            </label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={ayar.username ?? ''}
                                onChange={e => setAyar(p => ({ ...p, username: e.target.value }))}
                                placeholder="NetGSM kullanıcı adı"
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label" style={{ fontSize: 12, color: '#64748b' }}>
                                Şifre (password)
                            </label>
                            <div className="input-group input-group-sm">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className="form-control form-control-sm"
                                    value={ayar.password ?? ''}
                                    onChange={e => setAyar(p => ({ ...p, password: e.target.value }))}
                                    placeholder="NetGSM şifresi"
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => setShowPassword(p => !p)}
                                >
                                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                                </button>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label" style={{ fontSize: 12, color: '#64748b' }}>
                                Marka Kodu (brandCode)
                            </label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={ayar.brandCode ?? ''}
                                onChange={e => setAyar(p => ({ ...p, brandCode: e.target.value }))}
                                placeholder="Örn: 123456"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Butonlar */}
            <div className="d-flex gap-2 mt-3">
                <button
                    type="button"
                    className="btn btn-sm"
                    style={{ background: '#0f766e', color: '#fff', fontWeight: 600 }}
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving
                        ? <><i className="fas fa-spinner fa-spin me-2" />Kaydediliyor...</>
                        : <><i className="fas fa-save me-2" />Kaydet</>}
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleAktarSimdi}
                    disabled={aktariliyor}
                >
                    {aktariliyor
                        ? <><i className="fas fa-spinner fa-spin me-2" />Aktarılıyor...</>
                        : <><i className="fas fa-upload me-2" />Şimdi Aktar</>}
                </button>
            </div>

            {/* Aktarım sonuç paneli */}
            {aktarimSonuc && (
                <div className={`alert d-flex gap-2 mt-3 mb-0 ${
                    aktarimSonuc.sonuc?.hatali && aktarimSonuc.sonuc.hatali > 0
                        ? 'alert-warning'
                        : aktarimSonuc.uyari
                            ? 'alert-secondary'
                            : aktarimSonuc.sonuc?.basarili
                                ? 'alert-success'
                                : 'alert-info'
                }`} style={{ fontSize: 13 }}>
                    <i className={`fas mt-1 ${
                        aktarimSonuc.sonuc?.hatali && aktarimSonuc.sonuc.hatali > 0
                            ? 'fa-exclamation-triangle'
                            : aktarimSonuc.uyari
                                ? 'fa-info-circle'
                                : aktarimSonuc.sonuc?.basarili
                                    ? 'fa-check-circle'
                                    : 'fa-info-circle'
                    }`} />
                    <div style={{ flex: 1 }}>
                        <div className="fw-semibold mb-1">{aktarimSonuc.message}</div>
                        {aktarimSonuc.sonuc && aktarimSonuc.sonuc.toplamBekleyen > 0 && (
                            <div className="d-flex gap-3 mt-1">
                                <span style={{ color: '#166534' }}>
                                    <i className="fas fa-check me-1" />Başarılı: <strong>{aktarimSonuc.sonuc.basarili}</strong>
                                </span>
                                {aktarimSonuc.sonuc.hatali > 0 && (
                                    <span style={{ color: '#92400e' }}>
                                        <i className="fas fa-times me-1" />Hatalı: <strong>{aktarimSonuc.sonuc.hatali}</strong>
                                    </span>
                                )}
                            </div>
                        )}
                        {aktarimSonuc.sonuc?.hataDetaylari && aktarimSonuc.sonuc.hataDetaylari.length > 0 && (
                            <ul className="mb-0 mt-2 ps-3" style={{ fontSize: 12 }}>
                                {aktarimSonuc.sonuc.hataDetaylari.map((h, i) => (
                                    <li key={i} style={{ color: '#92400e' }}>{h}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <button type="button" onClick={() => setAktarimSonuc(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 4px', alignSelf: 'flex-start', fontSize: 14 }}>
                        <i className="fas fa-times" />
                    </button>
                </div>
            )}

            {/* Bilgi notu */}
            <div className="alert alert-info d-flex gap-2 mt-4" style={{ fontSize: 12 }}>
                <i className="fas fa-info-circle mt-1" />
                <div>
                    <strong>Otomatik aktarım:</strong> Bekleyen IYS kayıtları her 5 dakikada bir kontrol edilerek NetGSM IYS API'ye aktarılır.
                    Manuel aktarım için <strong>Şimdi Aktar</strong> butonunu kullanabilirsiniz.
                    IYS Aktarımı pasif olduğunda cariler ve müşteri detay sayfasındaki IYS kilidi özelliği gizlenir.
                </div>
            </div>
        </div>
    );
};

export default IysAktarimAyarlari;
